import { authFetch, type StreamGenConfig } from '@/api/request';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAgentChat } from '@/hooks/use-agent-chat';
import { useWebSocket } from '@/hooks/use-websocket';
import { ChatPanel } from '@/pages/Agent/components/chat-panel';
import { ChatInput } from '@/pages/Agent/components/chat-input';
import { Pipeline, BASE_STAGES, VIDEO_STAGES } from '@/components/pipeline';
import { PreviewFrame, type PreviewFrameHandle } from '@/components/preview-frame';
import { AnimationPicker } from '@/components/animation-picker';
import { ParticleCatalog } from '@/components/particle-catalog';
import { GameplaySelector } from '@/components/gameplay-selector';
import { SessionSidebar } from '@/components/session-sidebar';
import { DownloadDialog } from '@/components/download-dialog';
import { PublishDialog } from '@/components/publish-dialog';
import { getSessionPlayables, updatePlayable, cleanupHistoryVersions, buildShareUrl, type PlayableListItem } from '@/api/modules/playables';
import { MaterialPanel } from '@/components/material-panel';
import { GenerationConfigBar } from '@/components/generation-config-bar';
import { BatchVariantGrid, type BatchVariant } from '@/components/batch-variant-grid';

import { VIDEO_TO_PLAYABLE_PROMPT } from '@/data/quick-commands';
import { getGameplayPrompt, HONGBAO_EFFECT_PROMPT, type GameplayVariant } from '@/data/gameplay-prompts';
import { EmptyState } from '@/components/empty-state';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Tooltip } from '@/components/ui/tooltip';
import { findDuplicateInHistory } from '@/utils/duplicate-detection';
import { stripInternalMarkers } from '@/lib/message-markers';
import { createSession, getSessions, updateProject, updateSessionSelectedVersion, markGddConfirmed, markVideoConfirmed, markVideoPlanConfirmed, markChoiceSelected } from '@/api/modules/sessions';
import { uploadAsset, uploadAssetFile, uploadVideo, type CategorizedAssetItem } from '@/api/modules/assets';
import { useAgentStore } from '@/store/agent-store';
import { usePipelinePoll } from '@/hooks/use-pipeline-poll';
import { Loader2, ArrowLeft, ArrowDown, Play, GripVertical, Maximize, Link, Download, Upload, RotateCw, ChevronLeft, Film, Gamepad2, QrCode, AlertTriangle, Video, List, ChevronDown, Check, Trash2, Send, Eye, Sparkles } from 'lucide-react';
import { cn } from '@/utils/cn';
import { QRCodeSVG } from 'qrcode.react';
import type { PendingSend } from '@/utils/session-send-queue';

/** BUG-20260812-013: previewUrl 为相对路径（/preview/...），对外分享（二维码/复制链接）必须拼 origin，否则手机扫码/粘贴打不开 */
function toAbsolutePreviewUrl(url: string | null | undefined): string {
  if (!url) return '';
  return /^https?:\/\//.test(url) ? url : `${window.location.origin}${url}`;
}

// ===== Resize hook =====
const MIN_CHAT = 320; const MAX_CHAT = 700;
// 版本侧边栏固定宽度，移除 MIN_PIPE 和 MAX_PIPE
const MIN_PREVIEW = 240;

/**
 * useResizable — column resize with configurable direction.
 * @param direction 'left-edge' means dragging right increases width (pipeline, right edge of column).
 *                  'right-edge' means dragging left increases width (chat, left edge of column).
 */
function useResizable(
  defaultW: number, min: number, max: number,
  direction: 'left-edge' | 'right-edge' = 'left-edge',
) {
  const [width, setWidth] = useState(defaultW);
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const startX = e.clientX;
    const startW = width;
    const onMove = (ev: MouseEvent) => {
      const delta = direction === 'left-edge'
        ? ev.clientX - startX   // drag right → wider
        : startX - ev.clientX;  // drag left → wider
      setWidth(Math.min(max, Math.max(min, startW + delta)));
    };
    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [width, min, max, direction]);
  return { width, onMouseDown };
}

export function ProjectDetail(): JSX.Element {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { messages, loading, streaming, messagesLoading, sendMessage, abort, fetchMessages } = useAgentChat();
  const gddSpec = useAgentStore((s) => s.gddSpec);
  const gddConfirmed = useAgentStore((s) => s.gddConfirmed);
  const activeSessionId = useAgentStore((s) => s.activeSessionId);
  // BUG-20260729-003: 生成状态按会话隔离——仅当 streaming 所属会话 === 当前会话时才显示生成态
  const streamingSessionId = useAgentStore((s) => s.streamingSessionId);
  const isCurrentSessionStreaming = streaming && streamingSessionId === activeSessionId;
  const isCurrentSessionLoading = loading && streamingSessionId === activeSessionId;
  const [activeStepId, setActiveStepId] = useState<string>();
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [previewSize, setPreviewSize] = useState<number>();
  const [showDownload, setShowDownload] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  // 对话区「回到底部」：距底部超过阈值时显示悬浮按钮
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  // 消息加载/更新后重新评估按钮显隐（初始加载不触发 scroll 事件）
  useEffect(() => {
    const el = chatScrollRef.current;
    if (el) setShowScrollToBottom(el.scrollHeight - el.scrollTop - el.clientHeight > 80);
  }, [messages]);
  // BUG-20260820-SCROLL-001: 刷新/切换会话后消息加载完成时，把权威滚动容器 chatScrollRef 瞬时
  // 滚到底。ChatPanel 的 scrollIntoView(smooth) 在消息刚挂载、图片/卡片未完成布局时执行，
  // 滚动目标基于旧高度，动画期间内容继续撑高 → 停在中间。这里在加载完成瞬间多次兑底
  // （rAF×2 + 150ms + 600ms）覆盖异步图片布局，全部瞬时滚动（behavior 默认 auto）。
  const prevMessagesLoadingRef = useRef(false);
  useEffect(() => {
    const wasLoading = prevMessagesLoadingRef.current;
    prevMessagesLoadingRef.current = messagesLoading;
    if (wasLoading && !messagesLoading) {
      const scrollNow = () => {
        const el = chatScrollRef.current;
        if (el) el.scrollTo({ top: el.scrollHeight });
      };
      requestAnimationFrame(() => requestAnimationFrame(scrollNow));
      const t1 = window.setTimeout(scrollNow, 150);
      const t2 = window.setTimeout(scrollNow, 600);
      return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
    }
  }, [messagesLoading]);
  const [pendingSendsBySession, setPendingSendsBySession] = useState<Record<string, PendingSend[]>>({});
  const pendingSendsRef = useRef<Record<string, PendingSend[]>>({});
  const sendRunnerRef = useRef<(item: PendingSend) => Promise<void>>(async () => {});
  const sendRunningRef = useRef(false);

  // 版本管理
  const [playableVersions, setPlayableVersions] = useState<any[]>([]);
  const [currentVersionId, setCurrentVersionId] = useState<string>();

  // 过滤版本：只显示有实际产物的版本
  const filterValidVersions = useCallback((versions: any[]) => {
    return versions
      .filter((v: any) => v.previewUrl || v.htmlUrl)
      .sort((a: any, b: any) => {
        const versionDiff = Number(b.version ?? 0) - Number(a.version ?? 0);
        if (versionDiff !== 0) return versionDiff;
        return new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime();
      });
  }, []);

  const selectedVersionStorageKey = (sessionId: string): string => `playgen:selected-version:${sessionId}`;
  const persistVersionId = async (sessionId: string, versionId: string): Promise<void> => {
    try { window.localStorage.setItem(selectedVersionStorageKey(sessionId), versionId); } catch { /* storage unavailable */ }
    await updateSessionSelectedVersion(sessionId, versionId).catch(() => {
      // 本地选择已保存；服务端暂时不可用时下次加载仍可用本地版本锚点。
    });
  };

  // 选中版本的素材快照——所有版本都优先还原该版本生成时的素材，避免
  // 最新版本的实时 session 素材混入其他轮次/其他玩法；没有快照的历史版本再回退实时列表。
  const selectedVersion = playableVersions.find(v => v.id === currentVersionId);

  // P4 分享短链：复制链接/扫码一律用 /api/p/{分享ID}（与 OSS 物理路径解耦）；
  // 当前版本无分享 ID（如产物尚未落库）时退回预览直链
  // BUG-20260813-003: 优先短链（仅落库 12hex 分享 ID 有效）；微调版本等无短链时退回预览直链并拼 origin，
  // 否则相对路径 /preview/... 复制/扫码在手机上打不开
  const shareUrl = buildShareUrl(selectedVersion?.name) ?? toAbsolutePreviewUrl(previewUrl);

  // 使用 ref 避免闭包陷阱
  const currentVersionIdRef = useRef<string>();
  const playableVersionsRef = useRef<any[]>([]);
  const targetVersionIdRef = useRef<string | undefined>(); // 存储目标版本ID，防止被 useEffect 覆盖
  const loadSessionRequestRef = useRef(0);
  const versionsRequestRef = useRef(0);
  // BUG-20260812-017: 刷新时从历史微调任务恢复的 playableId——供下方微调锚定 effect 识别并跳过，
  // 避免把版本下拉抢回旧版本（刷新应锚定最新版本）
  const skipRefineAnchorRef = useRef<string | undefined>();

  // 监控 currentVersionId 变化，同步到 ref
  useEffect(() => {
    console.log('[ProjectDetail] currentVersionId 状态变化:', currentVersionId);
    currentVersionIdRef.current = currentVersionId;
    console.trace('[ProjectDetail] currentVersionId 变化堆栈');
  }, [currentVersionId]);

  // 版本只在“明确的用户/生成动作”中持久化（handleVersionChange、替换成功、微调/生成完成）。
  // 不要在 currentVersionId effect 中无条件写回：loadSession 会先清空旧状态，再异步恢复新会话，
  // 旧状态与新 activeSessionId 的一个渲染帧会把 V8 反向写入新会话，导致刷新后选中回退。

  // 监控 playableVersions 变化，同步到 ref
  useEffect(() => {
    console.log('[ProjectDetail] playableVersions 状态变化，长度:', playableVersions.length);
    playableVersionsRef.current = playableVersions;
  }, [playableVersions]);

  const previewFrameRef = useRef<PreviewFrameHandle>(null);
  const { nodes: pipelineNodes, nodesSessionId, pipeline: pipelinePauseState, timedOut: pipelineTimedOut, refresh: refreshPipeline } = usePipelinePoll(activeSessionId);
  // SSE 流水线事件驱动刷新：pipeline:stage/pipeline:paused 会 bump tick，这里监听后立即拉最新节点
  const pipelineTick = useAgentStore((s) => s.pipelineTick);
  const lastTickRef = useRef(0);
  useEffect(() => {
    if (pipelineTick !== lastTickRef.current) {
      lastTickRef.current = pipelineTick;
      refreshPipeline();
    }
  }, [pipelineTick]);

  // 微调状态（独立微调图）：预览区顶部徽章 + 完成/回滚时预览切换
  const refineState = useAgentStore((s) => s.refineState);
  const refineStatus = refineState.status;
  const refineStageLabel = refineState.stage === 'understand' ? '理解需求'
    : refineState.stage === 'apply' ? '应用修改' : '验证产物';
  // 微调完成/回滚的预览切换请求：chat 区 requestPreview(url) → 此处 HEAD 校验通过后 setPreviewUrl
  const previewRequest = useAgentStore((s) => s.previewRequest);
  const lastPreviewTickRef = useRef(0);
  // DO-NOT-REVERT: BUG-20260909-PW-003 切换前先 HEAD 探测产物可达性——微调构建产物尚未落盘时直接
  // set 会让 iframe 加载 404 黑屏（“微调后预览黑屏”链路之一）；同源失败延迟重试一次，仍失败保留
  // 旧预览（PW-003 预览链路：use-agent-chat finally 核对 + use-pipeline-poll done 切预览 + 此处 HEAD）
  const applyPreviewUrl = useCallback((url: string) => {
    const isAbsolute = /^https?:\/\//.test(url);
    const headUrl = isAbsolute ? url : `${window.location.origin}${url.startsWith('/') ? url : `/${url}`}`;
    const commit = () => {
      setPreviewUrl(url);
      // BUG-20260812-007: PreviewFrame 以 key={previewKey} 强制重挂载——只改 src 不 bump key 时 iframe 不重载（DO-NOT-REVERT）
      setPreviewKey(k => k + 1);
    };
    const check = (attempt: number) => {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 3000);
      fetch(headUrl, { method: 'HEAD', signal: ctrl.signal, cache: 'no-store' })
        .then((res) => {
          clearTimeout(timer);
          if (res.ok) commit();
          else if (attempt < 2) setTimeout(() => check(attempt + 1), 1500);
          else console.warn('[ProjectDetail] 预览产物不可达，保留旧预览:', headUrl, res.status);
        })
        .catch(() => {
          clearTimeout(timer);
          // 绝对 URL（OSS 等）跨域时 fetch 无法判定状态——不因校验阻断原有切换
          if (isAbsolute) { commit(); return; }
          if (attempt < 2) setTimeout(() => check(attempt + 1), 1500);
          else console.warn('[ProjectDetail] 预览产物校验失败，保留旧预览:', headUrl);
        });
    };
    check(1);
  }, []);
  useEffect(() => {
    if (previewRequest && previewRequest.tick !== lastPreviewTickRef.current) {
      lastPreviewTickRef.current = previewRequest.tick;
      applyPreviewUrl(previewRequest.url);
    }
  }, [previewRequest, applyPreviewUrl]);

  // 预览区状态文案：prototype_build 阶段显示加载动效，其他阶段静态等待
  const nodeValues = Object.values(pipelineNodes);
  const hasNodes = nodeValues.length > 0;
  const pipelineStages = pipelineNodes['video-start'] ? VIDEO_STAGES : BASE_STAGES;
  const activePipelineStage = pipelineStages.find((stage) =>
    stage.nodeKeys.some((key) => pipelineNodes[key]?.status === 'running'),
  );
  const completedPipelineStages = pipelineStages.filter((stage) =>
    stage.nodeKeys.some((key) => {
      const status = pipelineNodes[key]?.status;
      return status === 'done' || status === 'failed';
    }),
  ).length;
  // 节点已全部完成时，后端旧会话可能仍保留 pipeline.status=running。
  // 终态优先，避免工作台在 6/6 后继续显示旋转中的“生成流水线”。
  const allPipelineStagesComplete = hasNodes && completedPipelineStages >= pipelineStages.length;
  const pipelineTaskRunning = refineStatus === 'running'
    || (!allPipelineStagesComplete && (
      pipelinePauseState?.status === 'running'
      || nodeValues.some((node) => node.status === 'running' || node.status === 'pending')
    ));
  const pipelineProcessingLabel = refineStatus === 'running'
    ? '正在优化游戏意图'
    : activePipelineStage?.runningText ?? '生成流水线执行中';
  const conversationExecutionState = pipelineTaskRunning
    ? {
        title: pipelineProcessingLabel,
        detail: refineStatus === 'running'
          ? '意图优化完成前，新消息将进入待发送区'
          : `${completedPipelineStages}/${pipelineStages.length} 阶段完成 · 新消息将进入待发送区`,
      }
    : undefined;

  const buildNode = pipelineNodes.build ?? pipelineNodes.publish;
  const gameDevNode = pipelineNodes.prototype_build ?? pipelineNodes.game_develop_blueprint ?? pipelineNodes.game_develop;
  const gameDevRunning = gameDevNode?.status === 'running';
  const isGenerating = hasNodes && nodeValues.some((n) => n.status === 'running' || n.status === 'pending');
  const buildDone = buildNode?.status === 'done' || buildNode?.status === 'running';
  const previewStatusText = !hasNodes ? '待生成'
    : gameDevRunning ? '正在生成游戏...'
    : buildDone ? '等待预览加载'
    : isGenerating ? '等待游戏生成...'
    : '等待生成...';
  // 只在游戏开发阶段显示加载动画
  const previewShowSpinner = hasNodes && gameDevRunning;

  useEffect(() => {
    // BUG-20260821-001: 会话守卫——切换会话时 nodes 滞后一渲染帧（闭包旧值），
    // 无守卫会把旧会话 buildNode.previewUrl 回填进新（空）会话预览区
    if (nodesSessionId !== activeSessionId) return;
    const buildNode = pipelineNodes.build ?? pipelineNodes.publish;
    if (buildNode?.previewUrl && !previewUrl) {
      setPreviewUrl(buildNode.previewUrl as string);
      if (typeof buildNode.fileSize === 'number') setPreviewSize(buildNode.fileSize as number);
    }
  }, [pipelineNodes, previewUrl, nodesSessionId, activeSessionId]);
  const [sessionReady, setSessionReady] = useState(false);
  const [pipelineCollapsed, setPipelineCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('playgen.pipelineCollapsed');
      // 如果从未保存过，返回 null（等待首次出现节点时决定）
      if (saved === null || saved === undefined) return null;
      return saved === '1'; // '1' = 折叠，'0' = 展开
    } catch {
      return null;
    }
  });
  const togglePipeline = useCallback(() => {
    console.log('[ProjectDetail] togglePipeline 被调用');
    setPipelineCollapsed((v) => {
      const newValue = !v;
      console.log('[ProjectDetail] 从', v, '切换到', newValue);
      try { localStorage.setItem('playgen.pipelineCollapsed', newValue ? '1' : '0'); } catch { /* ignore */ }
      return newValue;
    });
  }, []);

  // 流水线节点出现时的自动展开逻辑
  const hasEverHadNodesRef = useRef(false);
  useEffect(() => {
    const hasNodes = Object.keys(pipelineNodes).length > 0;

    // 首次出现节点：自动展开并保存
    if (hasNodes && !hasEverHadNodesRef.current) {
      console.log('[ProjectDetail] 首次检测到流水线节点，自动展开');
      hasEverHadNodesRef.current = true;
      setPipelineCollapsed(false);
      try { localStorage.setItem('playgen.pipelineCollapsed', '0'); } catch { /* ignore */ }
    }
  }, [Object.keys(pipelineNodes).length]);

  const [projectName, setProjectName] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const saveTitle = async () => {
    if (titleValue.trim() && titleValue !== projectName) {
      setProjectName(titleValue.trim());
      if (projectId) updateProject(projectId, { name: titleValue.trim() }).catch(() => {});
    }
    setEditingTitle(false);
  };
  const initRef = useRef<string | null>(null);
  const [initError, setInitError] = useState(false);

  const { confirm, dialog: confirmDialog } = useConfirm();
  const chatResize = useResizable(650, MIN_CHAT, MAX_CHAT, 'right-edge');
  // 版本侧边栏固定宽度，不需要拖动调整
  const [showAnimations, setShowAnimations] = useState(false);
  const [showParticles, setShowParticles] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  // BUG-20260820-WEB-001: 网赚红包全局开关（聊天框上方），替代玩法清单每卡🧧按钮
  const [hongbaoOn, setHongbaoOn] = useState(false);
  // BUG-20260820-WEB-002: 记录最近一次玩法清单选中的变体，发送期识别内置红包变体隐式抑制
  const lastVariantRef = useRef<GameplayVariant | null>(null);
  const [showQr, setShowQr] = useState(false);
  const [published, setPublished] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  // DO-NOT-REVERT: BUG-20260805-014 — 「本项目素材」默认展开（8-05 修复未真正落地，2026-08-06 补落地）
  const [showMaterials, setShowMaterials] = useState(true);
  const [batch, setBatch] = useState<{ variants: BatchVariant[]; done: number; total: number; finished: boolean; totalTime?: number } | null>(null);
  const [prefill, setPrefill] = useState<{ text: string; nonce: number; mode?: 'append-hongbao' | 'remove-hongbao' }>();
  const [materialsNonce, setMaterialsNonce] = useState(0);
  const [previewW, setPreviewW] = useState(340);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const [versionDropdownOpen, setVersionDropdownOpen] = useState(false);
  const versionDropdownRef = useRef<HTMLDivElement>(null);

  /** 加载指定会话：切换 active、拉消息、恢复预览。 */
  const loadSession = useCallback(async (sid: string, targetVersionId?: string) => {
    const requestId = ++loadSessionRequestRef.current;
    const isCurrentRequest = () => requestId === loadSessionRequestRef.current
      && useAgentStore.getState().activeSessionId === sid;
    console.log('[ProjectDetail] loadSession 开始，会话ID:', sid, '目标版本ID:', targetVersionId);
    // 保存目标版本ID到 ref，防止被后续的 useEffect 覆盖
    targetVersionIdRef.current = targetVersionId;
    // DO-NOT-REVERT: BUG-20260729-003 切换会话前先 abort 旧会话的流，防止旧流事件污染新会话
    abort();
    setPreviewUrl(undefined);
    setPreviewSize(undefined);
    setPublished(false);
    setBatch(null);
    // 重置版本状态
    setPlayableVersions([]);
    setCurrentVersionId(undefined);
    currentVersionIdRef.current = undefined;
    console.log('[ProjectDetail] 版本状态已重置');
    useAgentStore.getState().resetGenConfig();
    // 微调状态按会话隔离：切换会话时重置，避免「微调中」徽章粘到新会话
    useAgentStore.getState().setRefineState({ status: 'idle', stage: undefined, baseVersion: undefined, basePreviewUrl: undefined, targetVersion: undefined, newPreviewUrl: undefined, playableId: undefined, error: undefined, nodeOutputs: undefined, thoughts: [] });
    useAgentStore.getState().setActiveSession(sid);
    // 同步会话 ID 到 URL，刷新时锚定
    navigate(`/creation/${projectId}?session=${sid}`, { replace: true });
    await fetchMessages(sid);
    try {
      const sRes = await authFetch(`/api/agent/session/${sid}`);
      const sData = await sRes.json();
      // 会话守卫：异步返回时用户可能已切走，避免旧会话预览覆盖当前会话（BUG-20260728-005）
      if (!isCurrentRequest()) return;
      const playables = sData?.data?.playables;
      if (playables?.length > 0) {
        // 排序口径与后端 /playables（version desc + createdAt desc）一致，防止 version 相同时与下方版本选中不一致导致预览闪切
        const latest = [...playables].sort((a: any, b: any) => Number(b.version ?? 0) - Number(a.version ?? 0) || (+new Date(b.createdAt ?? 0) - +new Date(a.createdAt ?? 0)))[0];
        setPreviewUrl(latest.previewUrl || latest.htmlUrl);
        // BUG-20260812-007 原则：所有预览切换点必须配套 bump previewKey 强制 iframe 重挂载，否则切回同 URL 会话时画面滞留（「预览对不上」）
        setPreviewKey(k => k + 1);
        setPreviewSize(latest.fileSize);
      }

      // 获取所有版本（包括草稿）
      try {
        const vRes = await authFetch(`/api/agent/session/${sid}/playables`);
        const vData = await vRes.json();
        console.log('[ProjectDetail] 版本列表加载:', vData);
        if (vData?.success && Array.isArray(vData.data)) {
          // 只显示有实际产物的版本（有 previewUrl 或 htmlUrl）
          const validVersions = filterValidVersions(vData.data);
          if (!isCurrentRequest()) return;
          setPlayableVersions(validVersions);
          console.log('[ProjectDetail] 设置版本列表（已过滤）:', validVersions);

          // 检查是否指定了目标版本。刷新锚定语义（BUG-20260812-017 检查点③）：
          // 无 URL targetVersionId / 手动替换标记时永远选 validVersions[0]（最新）。
          // localStorage/服务端旧选择不再参与刷新锚定——否则「最后持久化选择是旧版本」
          // 会把刷新锚回旧版本（用户报：手动替换后刷新锚 V10 而非最新 V11）。
          let selectedVersion = null;
          const manualReplaceId = sessionStorage.getItem(`manual_replace_${sid}`) || undefined;
          const persistedVersionId = targetVersionId || manualReplaceId;

          if (persistedVersionId) {
            // 查找指定的版本
            selectedVersion = validVersions.find((v: any) => v.id === persistedVersionId);
          }

          // 手动替换刚返回的新版本时，版本列表可能因读写延迟暂时还看不到 V9。
          // 此时绝不能把“最新”回退写成旧的 V8；保留服务端/会话的最新预览锚点，
          // 等下一次列表刷新再认领新版本。普通历史锚点失效时仍可回退到最新版本。
          const protectedSelection = Boolean(targetVersionId || manualReplaceId);
          if (!selectedVersion && !protectedSelection && validVersions.length > 0) {
            selectedVersion = validVersions[0];
            console.log('[ProjectDetail] 使用最新版本:', selectedVersion.id);
          }

          if (selectedVersion) {
            setCurrentVersionId(selectedVersion.id);
            currentVersionIdRef.current = selectedVersion.id;
            if (isCurrentRequest()) void persistVersionId(sid, selectedVersion.id);
            console.log('[ProjectDetail] 选中版本:', selectedVersion.id);
            setPreviewUrl(selectedVersion.previewUrl || selectedVersion.htmlUrl);
            // 同 BUG-20260812-007：会话版本错定后预览必须强制重挂载
            setPreviewKey(k => k + 1);
          }
        }
      } catch (err) {
        console.error('[ProjectDetail] 版本列表加载失败:', err);
      }

      // 获取微调任务历史，恢复未完成的任务状态
      try {
        const rtRes = await authFetch(`/api/agent/session/${sid}/refine-tasks`);
        const rtData = await rtRes.json();
        if (rtData?.success && Array.isArray(rtData.data) && rtData.data.length > 0) {
          if (!isCurrentRequest()) return;
          // 获取最新的一条微调任务
          const latestTask = rtData.data[0];
          console.log('[ProjectDetail] 恢复微调任务:', latestTask);

          // 如果任务还在进行中，恢复到 store
          if (latestTask.status === 'thinking' || latestTask.status === 'applying' || latestTask.status === 'verifying') {
            // BUG-20260817-042: 僵尸任务判定——微调图随 SSE 断连（页面刷新）被杀时终态不落库，
            // RefineTask 永停中间态；超 10 分钟（与后端 activeRefines stale 自愈阈值一致，
            // 整轮最长 8 分钟不会误伤正常任务）仍“进行中”视为已死，按 failed 恢复，
            // 否则恢复 running 后轮询永拿不到终态，卡片永久转圈
            const taskAgeMs = Date.now() - new Date(latestTask.updatedAt || latestTask.createdAt).getTime();
            if (Number.isFinite(taskAgeMs) && taskAgeMs > 10 * 60_000) {
              useAgentStore.getState().setRefineState({
                status: 'failed',
                stage: 'verify',
                playableId: latestTask.playableId,
                error: '意图优化已中断（执行期间页面刷新或服务重启），请重新发起',
              });
            } else {
            const progress = latestTask.progress || {};
            useAgentStore.getState().setRefineState({
              status: 'running',
              stage: progress.stage || 'understand',
              nodeOutputs: progress.nodeOutputs,
              baseVersion: undefined,
              basePreviewUrl: undefined,
              playableId: latestTask.playableId,
            });
            }
          } else if (latestTask.status === 'done' && latestTask.result) {
            // 任务已完成，恢复完成状态
            const result = latestTask.result;
            // BUG-20260819-002: 优先检查手动替换标记，防止被旧微调任务抢回旧版本
            const manualReplaceId = sessionStorage.getItem(`manual_replace_${sid}`);
            if (manualReplaceId) {
              // 手动替换优先级最高，清除标记并跳过此次微调产物
              skipRefineAnchorRef.current = manualReplaceId;
              if (latestTask.playableId) skipRefineAnchorRef.current = latestTask.playableId;
              sessionStorage.removeItem(`manual_replace_${sid}`);
              sessionStorage.removeItem(`manual_replace_refine_${sid}`);
              // BUG-20260819-FIX: 手动替换后不恢复旧的微调状态，避免 refinePlayableId 触发锚定
            } else {
              // BUG-20260812-017: 记录恢复出的旧产物 ID，微调锚定 effect 见到它时跳过——
              // 否则刷新后旧任务 playableId 会把版本下拉从最新（重新生成产物）抢回旧版本
              skipRefineAnchorRef.current = latestTask.playableId;
              useAgentStore.getState().setRefineState({
                status: 'done',
                stage: 'verify',
                newPreviewUrl: result.previewUrl,
                playableId: latestTask.playableId,
                nodeOutputs: result.nodeOutputs,
              });
            }
          }
        }
        // 版本列表/微调历史均已完成读取后再消费手动替换标记，避免旧微调任务在同一
        // 次刷新中把刚选中的 V9 抢回 V8。
        if (isCurrentRequest()) {
          sessionStorage.removeItem(`manual_replace_${sid}`);
          sessionStorage.removeItem(`manual_replace_refine_${sid}`);
        }
      } catch (err) {
        console.error('[ProjectDetail] 微调任务加载失败:', err);
      }
    } catch { /* no prior playable */ }
  }, [fetchMessages, projectId, navigate, abort]);

  /** 切换 Playable 版本 */
  const handleVersionChange = useCallback((versionId: string) => {
    const version = playableVersions.find(v => v.id === versionId);
    if (version?.previewUrl) {
      setCurrentVersionId(versionId);
      currentVersionIdRef.current = versionId;
      if (activeSessionId) persistVersionId(activeSessionId, versionId);
      setPreviewUrl(version.previewUrl);
      setPreviewKey(prev => prev + 1); // 强制刷新 iframe
    }
  }, [playableVersions, activeSessionId]);

  /** 重新加载版本列表 */
  const reloadVersions = useCallback(async (sessionId: string) => {
    const requestId = ++versionsRequestRef.current;
    try {
      const vRes = await authFetch(`/api/agent/session/${sessionId}/playables`);
      const vData = await vRes.json();
      if (requestId !== versionsRequestRef.current || useAgentStore.getState().activeSessionId !== sessionId) return;
      console.log('[ProjectDetail] 重新加载版本列表:', vData);
      if (vData?.success && Array.isArray(vData.data)) {
        const validVersions = filterValidVersions(vData.data);
        setPlayableVersions(validVersions);
        // 如果当前没有选中版本，选中最新的
        // 但如果有目标版本ID（从URL跳转过来），优先使用目标版本
        if (!currentVersionIdRef.current && validVersions.length > 0) {
          if (targetVersionIdRef.current) {
            const targetVersion = validVersions.find(v => v.id === targetVersionIdRef.current);
            if (targetVersion) {
              console.log('[ProjectDetail] 使用目标版本:', targetVersionIdRef.current);
              setCurrentVersionId(targetVersion.id);
              currentVersionIdRef.current = targetVersion.id;
              targetVersionIdRef.current = undefined; // 使用后清除
              return;
            }
          }
          // 没有目标版本或找不到：直接锚定最新版本（刷新语义永远选最新，
          // BUG-20260812-017 检查点③；不再用 localStorage 旧选择恢复，避免锚回旧版本）。
          const nextVersion = validVersions[0];
          setCurrentVersionId(nextVersion.id);
          currentVersionIdRef.current = nextVersion.id;
          persistVersionId(sessionId, nextVersion.id);
        }
      }
    } catch (err) {
      console.error('[ProjectDetail] 重新加载版本列表失败:', err);
    }
  }, [filterValidVersions]);

  // 监听微调状态变化，当有新的 playableId 时重新加载版本列表
  // BUG-20260812-007: 重载后必须锚定选中到新版本——reloadVersions 仅在「无选中」时设选中，
  // 微调产生 V(n+1) 后选中永停旧版本（用户报「不重新锚定新版本」）
  const refinePlayableId = refineState.playableId;
  const lastRefinePlayableRef = useRef<string>();
  useEffect(() => {
    if (refinePlayableId && refinePlayableId !== lastRefinePlayableRef.current && activeSessionId) {
      lastRefinePlayableRef.current = refinePlayableId;
      // BUG-20260812-017: 该 playableId 来自刷新恢复的历史微调任务时跳过锚定——
      // 刷新必须锚定最新版本（loadSession validVersions[0]），不能被旧任务抢回旧版本；
      // 正常微调流程（running→done 新产物）不受影响（BUG-20260812-007 锚定行为保留）
      if (skipRefineAnchorRef.current === refinePlayableId) {
        skipRefineAnchorRef.current = undefined;
        return;
      }
      // 手动替换先于版本列表异步刷新完成时，旧微调 playableId 仍可能触发本 effect。
      // 以替换回调写入的 superseded id 做一次性屏障，避免新版本先显示后又跳回旧版本。
      if (activeSessionId) {
        const supersededRefineId = sessionStorage.getItem(`manual_replace_refine_${activeSessionId}`);
        if (supersededRefineId && supersededRefineId === refinePlayableId) {
          return;
        }
      }
      // BUG-20260819-002: 如果当前已选中比 refinePlayableId 更新的版本，跳过锚定
      // （手动替换产生的新版本不应被旧的微调产物 ID 拉回）
      if (currentVersionIdRef.current && playableVersionsRef.current.length > 0) {
        const currentIndex = playableVersionsRef.current.findIndex(v => v.id === currentVersionIdRef.current);
        const refineIndex = playableVersionsRef.current.findIndex(v => v.id === refinePlayableId);
        // versions 按时间倒序，index 小 = 更新
        if (currentIndex >= 0 && refineIndex >= 0 && currentIndex < refineIndex) {
          return;
        }
      }
      console.log('[ProjectDetail] 检测到新微调产物，重载版本列表并锚定新版本');
      // 延迟一下确保后端已经写入数据库
      setTimeout(async () => {
        try {
          // BUG-20260820-ANCHOR-002: effect 主体检查时版本列表/当前选中可能尚未就绪（刷新
          // 期间 usePipelinePoll 同步历史 refine done 状态早于 loadSession 锚定），首次检查
          // 全部漏过 → 500ms 回调把已锚定的 V11 抢回 V10。回调内必须再做一遍防护：
          // ① 历史微调产物（刷新恢复）→ 跳过；② 当前已选版本比 refinePlayableId 更新 → 跳过。
          if (skipRefineAnchorRef.current === refinePlayableId) {
            skipRefineAnchorRef.current = undefined;
            return;
          }
          if (currentVersionIdRef.current && playableVersionsRef.current.length > 0) {
            const currentIndex = playableVersionsRef.current.findIndex(v => v.id === currentVersionIdRef.current);
            const refineIndex = playableVersionsRef.current.findIndex(v => v.id === refinePlayableId);
            // versions 按时间倒序，index 小 = 更新
            if (currentIndex >= 0 && refineIndex >= 0 && currentIndex < refineIndex) {
              return;
            }
          }
          const vRes = await authFetch(`/api/agent/session/${activeSessionId}/playables`);
          const vData = await vRes.json();
          if (vData?.success && Array.isArray(vData.data)) {
            const validVersions = filterValidVersions(vData.data);
            // BUG-20260820-ANCHOR-002: 请求等待期间 loadSession 可能刚完成锚定/恢复——
            // 以刚拉到的列表与最新选中做权威判断，防止已锚定的新版本被旧微调产物抢回。
            if (skipRefineAnchorRef.current === refinePlayableId) {
              skipRefineAnchorRef.current = undefined;
              return;
            }
            if (currentVersionIdRef.current) {
              const currentIndex = validVersions.findIndex(v => v.id === currentVersionIdRef.current);
              const refineIndex = validVersions.findIndex(v => v.id === refinePlayableId);
              // versions 按时间倒序，index 小 = 更新
              if (currentIndex >= 0 && refineIndex >= 0 && currentIndex < refineIndex) {
                return;
              }
            }
            setPlayableVersions(validVersions);
            const nv = validVersions.find((v: any) => v.id === refinePlayableId);
            if (nv?.previewUrl) {
              setCurrentVersionId(nv.id);
              currentVersionIdRef.current = nv.id;
              setPreviewUrl(nv.previewUrl);
              setPreviewKey(k => k + 1);
            }
          }
        } catch (err) {
          console.error('[ProjectDetail] 重新加载版本列表失败:', err);
        }
      }, 500);
    }
  }, [refinePlayableId, activeSessionId, filterValidVersions]);

  /** 接受草稿版本（发布为正式版） */
  const handleAcceptDraft = useCallback(async (versionId: string) => {
    try {
      const res = await authFetch(`/api/agent/playable/${versionId}/publish`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success('草稿已发布为正式版本');
        // 重新加载版本列表
        if (activeSessionId) {
          const vRes = await authFetch(`/api/agent/session/${activeSessionId}/playables`);
          const vData = await vRes.json();
          if (vData?.success) {
            setPlayableVersions(filterValidVersions(vData.data));
          }
        }
      } else {
        toast.error(data.message || '发布失败');
      }
    } catch (err) {
      toast.error('发布失败');
      console.error(err);
    }
  }, [activeSessionId]);

  /** 丢弃草稿版本 */
  const handleDiscardDraft = useCallback(async (versionId: string) => {
    try {
      const res = await authFetch(`/api/agent/playable/${versionId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        toast.success('草稿已丢弃');
        // 重新加载版本列表
        if (activeSessionId) {
          const vRes = await authFetch(`/api/agent/session/${activeSessionId}/playables`);
          const vData = await vRes.json();
          if (vData?.success) {
            const validVersions = filterValidVersions(vData.data);
            setPlayableVersions(validVersions);
            // 如果丢弃的是当前版本，切换到最新的正式版
            if (currentVersionId === versionId && validVersions.length > 0) {
              const publishedVersions = validVersions.filter((v: any) => v.status === 'published');
              if (publishedVersions.length > 0) {
                handleVersionChange(publishedVersions[0].id);
              }
            }
          }
        }
      } else {
        toast.error(data.message || '删除失败');
      }
    } catch (err) {
      toast.error('删除失败');
      console.error(err);
    }
  }, [activeSessionId, currentVersionId, handleVersionChange]);

  /** 切换到已有会话（侧栏点击）。 */
  const handleSelectSession = useCallback((sid: string) => {
    if (sid === useAgentStore.getState().activeSessionId) return;
    loadSession(sid);
  }, [loadSession]);

  /** 新建会话并切换。 */
  const handleCreateSession = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await createSession(projectId);
      const sid = res.data.id;
      // 乐观插入侧栏（插到最前，与「最新创建在最上面」排序约定一致），避免列表刷新失败时新会话不可见（BUG-20260729-002）
      const now = new Date().toISOString();
      useAgentStore.setState((s) => (s.sessionList.some((x) => x.id === sid) ? s : { sessionList: [{ id: sid, title: null, projectId, status: 'active', createdAt: now, updatedAt: now }, ...s.sessionList] }));
      await useAgentStore.getState().fetchSessionList(projectId);
      await loadSession(sid);
    } catch { toast.error('创建会话失败'); }
  }, [projectId, loadSession]);

  /** 根据 gddSpec 生成默认试玩名称 */
  const generateDefaultPublishName = useCallback(() => {
    if (!gddSpec) return `试玩-${Date.now()}`;
    const theme = (gddSpec.theme as string) ?? '';
    const gameplayName = (gddSpec.gameplayName as string) ?? '';
    const gameplayId = (gddSpec.gameplayId as string) ?? '';
    const parts = [theme, gameplayName || gameplayId].filter(Boolean);
    return parts.length > 0 ? `${parts.join('')}试玩` : `试玩-${Date.now()}`;
  }, [gddSpec]);

  /** 打开发布弹窗 */
  const handlePublish = useCallback(() => {
    if (!previewUrl) {
      toast.error('暂无试玩内容可发布');
      return;
    }
    setPublishDialogOpen(true);
  }, [previewUrl]);

  /** 基于已确认 GDD 重新生成一版新试玩（绕过生图缓存，引入受控变化） */
  const handleRegenerate = useCallback(() => {
    if (!activeSessionId) {
      toast.error('会话尚未就绪');
      return;
    }
    if (!previewUrl || !gddConfirmed) {
      toast.error('暂无可重新生成的试玩');
      return;
    }
    if (sendRunningRef.current || streaming || pipelineTaskRunning) {
      toast.info('当前正在生成中，请稍候');
      return;
    }
    confirm(
      '重新生成试玩',
      '将基于已确认方案重新生成一版新试玩（素材与细节会有变化），是否继续？',
      () => {
        const genConfig: StreamGenConfig = {
          ...useAgentStore.getState().genConfig,
          duplicateChoice: 'regenerate',
          ...(currentVersionIdRef.current ? { baseVersionId: currentVersionIdRef.current } : {}),
        };
        void sendMessage('重新生成', activeSessionId, true, genConfig);
        toast.info('已开始重新生成，左侧可查看进度');
      },
    );
  }, [activeSessionId, confirm, gddConfirmed, pipelineTaskRunning, previewUrl, sendMessage, streaming]);

  /** 真实发布：将本会话最新试玩置为 published（持久化），刷新后仍为已发布且在试玩列表/资产中展示 */
  const handlePublishConfirm = useCallback(async (name: string, clearHistory: boolean) => {
    if (!activeSessionId) return;
    setPublishing(true);
    try {
      const res = await getSessionPlayables(activeSessionId);
      const target = res.data?.[0]; // 后端按 createdAt desc 返回，最新试玩在前
      if (!target) throw new Error('no playable to publish');
      // DO-NOT-REVERT: BUG-20260805-017 — 发布必须真实 PATCH 持久化 status='published'，
      // 禁止退回 mock（setTimeout + 本地 state）导致刷新后状态回退
      await updatePlayable(target.id, { status: 'published', variantLabel: name });
      setPublished(true);
      setPublishDialogOpen(false);
      if (clearHistory) {
        // 发布后清空历史版本：仅留当前版本为基准 + 清临时产物
        try {
          await cleanupHistoryVersions(activeSessionId, target.id);
          setCurrentVersionId(target.id);
          currentVersionIdRef.current = target.id;
          if (target.previewUrl) { setPreviewUrl(target.previewUrl); setPreviewKey(k => k + 1); }
          toast.success(`发布成功！已清空历史版本，仅保留基准版本`);
        } catch {
          toast.success(`发布成功！但清空历史版本失败，请重试`);
        }
      } else {
        toast.success(`发布成功！试玩名称：${name}`);
      }
      // 刷新版本下拉列表（展示最新发布状态与用户输入的名称）
      reloadVersions(activeSessionId);
    } catch {
      toast.error('发布失败，请确认后端服务已启动');
    } finally {
      setPublishing(false);
    }
  }, [activeSessionId, reloadVersions]);

  const commitPendingSends = useCallback((next: Record<string, PendingSend[]>) => {
    pendingSendsRef.current = next;
    setPendingSendsBySession(next);
  }, []);

  const enqueueSend = useCallback((item: PendingSend, notify = true) => {
    const current = pendingSendsRef.current[item.sessionId] ?? [];
    commitPendingSends({ ...pendingSendsRef.current, [item.sessionId]: [...current, item] });
    if (notify) toast.info('已加入待发送，当前任务完成后会自动发送');
  }, [commitPendingSends]);

  const takeNextPending = useCallback((sessionId = useAgentStore.getState().activeSessionId): PendingSend | null => {
    if (!sessionId) return null;
    const nextItem = pendingSendsRef.current[sessionId]?.[0];
    if (!nextItem) return null;
    const remaining = (pendingSendsRef.current[sessionId] ?? []).filter((item) => item.id !== nextItem.id);
    const nextState = { ...pendingSendsRef.current };
    if (remaining.length) nextState[sessionId] = remaining;
    else delete nextState[sessionId];
    commitPendingSends(nextState);
    return nextItem;
  }, [commitPendingSends]);

  const removeQueuedSend = useCallback((id: string) => {
    const nextState: Record<string, PendingSend[]> = {};
    for (const [sessionId, items] of Object.entries(pendingSendsRef.current)) {
      const remaining = items.filter((item) => item.id !== id);
      if (remaining.length) nextState[sessionId] = remaining;
    }
    commitPendingSends(nextState);
  }, [commitPendingSends]);

  /** 上传附件并执行单条消息；由全局串行 runner 调用，避免 sendMessage 互相 abort。 */
  const executeSend = useCallback(async (item: PendingSend) => {
    let uploadedImages: Array<{ assetId: string; url: string; fileName: string; frames?: string[]; type?: string; subcategory?: string }> | undefined;
    let uploadedVideos: Array<{ filePath: string; url?: string; fileName: string; originalName: string }> | undefined;
    let uploadedVideoPaths: string[] | undefined;

    if (item.files.length) {
      const images: Array<{ assetId: string; url: string; fileName: string; type?: string }> = [];
      const videos: Array<{ filePath: string; url?: string; fileName: string; originalName: string }> = [];
      const videoPaths: string[] = [];
      for (const file of item.files) {
        if (file.type.startsWith('image/')) {
          try {
            const { id, url } = await uploadAssetFile(file, { category: 'custom', sessionId: item.sessionId });
            images.push({ assetId: id, url, fileName: file.name });
            // BUG-20260821-VIDEO-DUP-ENDCARD: 聊天上传的结束页意图图片不再双路由到 endCardFilePaths——
            // 素材确认卡片已将其展示为「结束页背景图」可编辑槽位（文件名推断初值），确认后经
            // 角色链路（applyUploadedImageRoles）注入结束页背景；双路由会导致卡片底部重复展示
            // 「编码文件名 + 结束页背景（显式上传）」行。配置栏「结束页」按钮的显式上传仍走 endCardFilePaths。
          } catch { toast.error(`图片 ${file.name} 上传失败`); }
        } else if (file.type.startsWith('video/')) {
          try {
            const { filePath, url } = await uploadVideo(file);
            if (filePath) {
              videoPaths.push(filePath);
              videos.push({ filePath, url, fileName: file.name, originalName: file.name });
            }
          } catch { toast.error(`视频 ${file.name} 上传失败`); }
        } else if (file.type.startsWith('audio/')) {
          // 音频附件：随消息透传 type='audio'，视频转试玩素材确认卡片展示音效/BGM 槽位
          try {
            const { id, url } = await uploadAssetFile(file, { category: 'custom', sessionId: item.sessionId });
            images.push({ assetId: id, url, fileName: file.name, type: 'audio' });
          } catch { toast.error(`音频 ${file.name} 上传失败`); }
        } else {
          void uploadAsset(file, { sessionId: item.sessionId });
        }
      }
      if (images.length) uploadedImages = images;
      if (videos.length) uploadedVideos = videos;
      if (videoPaths.length) uploadedVideoPaths = videoPaths;
    }

    if (item.libraryAssets.length) {
      const libraryImages = item.libraryAssets.map((asset) => ({
        assetId: asset.id,
        url: asset.previewUrl ?? asset.url,
        fileName: asset.name || 'library-asset',
        frames: asset.frames,
        // 音频资产（音效/BGM）透传 type/subcategory，后端拆入素材确认卡片音频槽位
        ...(asset.type === 'audio' ? { type: 'audio', subcategory: asset.subcategory } : {}),
      }));
      uploadedImages = [...(uploadedImages ?? []), ...libraryImages];
    }

    const genConfig = useAgentStore.getState().genConfig;
    // BUG-20260909-PW-001/003: 此处不整体白名单过滤 genConfig——配置栏（generation-config-bar）
    // 当轮写入的 videoFilePaths 是视频转试玩首条请求的必需入参；脏字段/过期路径已在恢复源封堵
    // （fetchMessages 恢复走 pickGenConfig 白名单，userImageRoles/videoFilePaths 均不进入 store），
    // 后端 handleVideoDirect 亦已收紧（需本轮新上传才视为新视频请求），双保险下无需发送前再裁。
    const mergedVideoPaths = [...new Set([...(genConfig.videoFilePaths ?? []), ...(uploadedVideoPaths ?? [])])];
    // 结束页仅透传配置栏显式上传；聊天附件图走图片槽位角色链路（见 executeSend 图片分支注释）
    const mergedEndcardPaths = [...(genConfig.endCardFilePaths ?? [])];
    const config = {
      ...genConfig,
      ...(uploadedImages ? { uploadedImages } : {}),
      ...(uploadedVideos ? { uploadedVideos } : {}),
      ...(mergedVideoPaths.length ? { videoFilePaths: mergedVideoPaths } : {}),
      ...(mergedEndcardPaths.length ? { endCardFilePaths: mergedEndcardPaths } : {}),
      ...(item.baseVersionId ? { baseVersionId: item.baseVersionId } : {}),
    };
    await sendMessage(item.text, item.sessionId, false, config);
  }, [sendMessage]);

  const runSend = useCallback(async (item: PendingSend) => {
    if (sendRunningRef.current || useAgentStore.getState().streaming || pipelineTaskRunning) {
      enqueueSend(item, false);
      return;
    }
    sendRunningRef.current = true;
    try {
      await executeSend(item);
    } finally {
      sendRunningRef.current = false;
      if (pipelineTaskRunning || useAgentStore.getState().streaming) return;
      const next = takeNextPending();
      if (next) void sendRunnerRef.current(next);
    }
  }, [enqueueSend, executeSend, takeNextPending, pipelineTaskRunning]);
  sendRunnerRef.current = runSend;

  // GDD 确认等非输入框操作也可能占用 SSE；它结束后继续消费待发送队列。
  useEffect(() => {
    if (streaming || loading || pipelineTaskRunning || sendRunningRef.current) return;
    const next = takeNextPending();
    if (next) void sendRunnerRef.current(next);
  }, [streaming, loading, pipelineTaskRunning, takeNextPending]);

  /** 停止当前会话的全部生成流程：中断 SSE、取消后端队列/流水线，并刷新前端状态。 */
  const handleStop = useCallback(() => {
    const sessionId = activeSessionId;
    abort(sessionId);
    if (sessionId) {
      const nextState = { ...pendingSendsRef.current };
      delete nextState[sessionId];
      commitPendingSends(nextState);
      useAgentStore.getState().bumpPipelineTick();
      refreshPipeline();
    }
    const rs = useAgentStore.getState().refineState;
    if (rs.status === 'running') {
      useAgentStore.getState().setRefineState({ ...rs, status: 'failed', error: '用户已停止' });
    }
    toast.info('已停止当前生成');
  }, [abort, activeSessionId, commitPendingSends, refreshPipeline]);

  /** 发送消息：运行中进入待发送队列，否则先做重复指令检测再串行执行。 */
  const handleSend = useCallback((text: string, files?: File[], libraryAssets?: CategorizedAssetItem[]) => {
    const sessionId = useAgentStore.getState().activeSessionId;
    if (!sessionId) {
      toast.error('会话尚未就绪，请稍候重试');
      return;
    }
    // BUG-20260820-WEB-002: 发送期归一化网赚红包标记——
    // 内置红包变体：隐式注入 [NO_EARN_FX]（输入框不可见）防 detectEarnFx 叠加；
    // 带标记文本按开关重写；自由文本仅开关 ON 时叠加特效层，OFF 保持原行为（detectEarnFx 可触发）
    const HONGBAO_MARKER = '【网赚红包特效层】';
    const isBuiltinHongbao = !!lastVariantRef.current && lastVariantRef.current.id.startsWith('hongbao');
    let normalized = text.trim();
    if (normalized.includes('[NO_EARN_FX]') || normalized.includes(HONGBAO_MARKER)) {
      normalized = normalized.replace(/\s*\[NO_EARN_FX\]/g, '').split(HONGBAO_EFFECT_PROMPT)[0].trimEnd();
      if (isBuiltinHongbao) {
        normalized += '\n[NO_EARN_FX]';
      } else if (hongbaoOn) {
        normalized += HONGBAO_EFFECT_PROMPT;
      }
    } else if (isBuiltinHongbao) {
      normalized += '\n[NO_EARN_FX]';
    } else if (hongbaoOn) {
      normalized += HONGBAO_EFFECT_PROMPT;
    } else {
      normalized += '\n[NO_EARN_FX]';
    }
    // 内部技术标记（如 [NO_EARN_FX]）仅供 LLM 消费，用户不应在对话区看到
    const displayText = stripInternalMarkers(normalized);
    const item: PendingSend = {
      id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      sessionId,
      text: normalized,           // wireMessage：发给后端的完整指令（含技术标记）
      displayText,                // 落库/展示用文本（剥离标记）
      files: files ?? [],
      libraryAssets: libraryAssets ?? [],
      baseVersionId: currentVersionIdRef.current,
      createdAt: Date.now(),
    };

    if (sendRunningRef.current || useAgentStore.getState().streaming || pipelineTaskRunning) {
      enqueueSend(item);
      return;
    }

    if (item.text && item.files.length === 0 && item.libraryAssets.length === 0) {
      const duplicate = findDuplicateInHistory(item.text, messages);
      if (duplicate?.found) {
        const description = duplicate.isLatest
          ? '你的需求与上一条一致，是否重新生成？'
          : '本次对话中已提过相同需求，是否重新生成？';
        confirm('重复指令', description, () => { void sendRunnerRef.current(item); });
        return;
      }
    }
    void sendRunnerRef.current(item);
  }, [messages, confirm, enqueueSend, pipelineTaskRunning, hongbaoOn]);

  /**
   * 初始化项目会话：复用最新会话，无则自动新建并立即展示在侧栏；失败可重试。
   * DO-NOT-REVERT(BUG-20260729-002): 失败必须置 initError 供重试入口，不得退回「仅 toast 后永久卡初始化」。
   */
  const initProject = useCallback(async () => {
    if (!projectId) return;
    setInitError(false);
    try {
      // Load session list for the sidebar
      await useAgentStore.getState().fetchSessionList(projectId);
      const sessions = await getSessions(projectId);
      // 从 URL 读取锚定的会话 ID 和版本 ID
      const urlParams = new URLSearchParams(window.location.search);
      const urlSessionId = urlParams.get('session');
      const urlVersionId = urlParams.get('version');
      let sid: string;
      if (urlSessionId && sessions.some((s) => s.id === urlSessionId)) {
        sid = urlSessionId;
      } else if (sessions.length > 0) {
        sid = sessions[sessions.length - 1].id;
      } else {
        const res = await createSession(projectId);
        sid = res.data.id;
        // 乐观插入侧栏保证「进入项目即见会话」，refetch 仅做对账（BUG-20260729-002）
        const now = new Date().toISOString();
        useAgentStore.setState((s) => (s.sessionList.some((x) => x.id === sid) ? s : { sessionList: [...s.sessionList, { id: sid, title: null, projectId, status: 'active', createdAt: now, updatedAt: now }] }));
        await useAgentStore.getState().fetchSessionList(projectId);
      }
      await loadSession(sid, urlVersionId || undefined);

      // Load project name
      try {
        const pRes = await authFetch(`/api/projects`);
        const pData = await pRes.json();
        const project = pData?.data?.find((p: { id: string }) => p.id === projectId);
        if (project) setProjectName(project.name);
      } catch {}

      setSessionReady(true);
    } catch {
      setInitError(true);
      toast.error('会话初始化失败，请重试');
    }
  }, [projectId, loadSession]);

  useEffect(() => {
    // 按 projectId 去重：同项目只初始化一次，切换项目时重新初始化（BUG-20260729-002）
    if (!projectId || initRef.current === projectId) return;
    initRef.current = projectId;
    setSessionReady(false);
    initProject();
  }, [projectId, initProject]);

  const sessionList = useAgentStore((s) => s.sessionList);

  useEffect(() => {
    if (!sessionReady) return;
    const validSessionIds = new Set(sessionList.map((session) => session.id));
    const nextState = Object.fromEntries(
      Object.entries(pendingSendsRef.current).filter(([sessionId]) => validSessionIds.has(sessionId)),
    );
    if (Object.keys(nextState).length !== Object.keys(pendingSendsRef.current).length) {
      commitPendingSends(nextState);
    }
  }, [sessionList, sessionReady, commitPendingSends]);

  // 删除当前会话后自动切换到第一个剩余会话，全部删完则清空 URL
  useEffect(() => {
    if (!activeSessionId || !sessionReady) return;
    if (sessionList.length === 0) {
      navigate(`/creation/${projectId}`, { replace: true });
    } else if (!sessionList.some((s) => s.id === activeSessionId)) {
      loadSession(sessionList[0].id);
    }
  }, [sessionList, activeSessionId, sessionReady, projectId, loadSession, navigate]);

  const { subscribe } = useWebSocket(activeSessionId);

  // 视频确认节点写入消息后没有聊天 SSE；收到节点完成事件时同步拉消息，确认卡无需刷新页面。
  useEffect(() => {
    const unsub = subscribe('pipeline:stage', (payload: unknown) => {
      refreshPipeline();
      const nodeOutputs = (payload as { nodeOutputs?: Record<string, { status?: string }> })?.nodeOutputs;
      if (nodeOutputs?.['video-confirmation']?.status === 'done' && activeSessionId) {
        fetchMessages(activeSessionId).catch(() => {});
      }
    });
    return unsub;
  }, [subscribe, refreshPipeline, fetchMessages, activeSessionId]);

  useEffect(() => {
    const unsub = subscribe('playable:ready' as never, async (data: unknown) => {
      const d = data as Record<string, unknown>;
      const url = (d.previewUrl as string) ?? (d.htmlUrl as string);
      console.log('[ProjectDetail] playable:ready 事件触发:', { url, data: d });

      if (url) {
        setPublished(false); // 新试玩就绪，需要重新发布

        // 微调完成后自动刷新版本列表并锚定到最新版本
        if (activeSessionId) {
          try {
            console.log('[ProjectDetail] 开始刷新版本列表...');
            const vRes = await authFetch(`/api/agent/session/${activeSessionId}/playables`);
            const vData = await vRes.json();
            console.log('[ProjectDetail] 版本列表响应:', vData);

            if (vData?.success && Array.isArray(vData.data) && vData.data.length > 0) {
              const validVersions = filterValidVersions(vData.data);
              setPlayableVersions(validVersions);
              // 锚定到最新版本（第一个，因为后端按时间倒序返回）
              if (validVersions.length > 0) {
                const latestVersion = validVersions[0];
                setCurrentVersionId(latestVersion.id);
                currentVersionIdRef.current = latestVersion.id;

                const newPreviewUrl = latestVersion.previewUrl || latestVersion.htmlUrl || url;
                // 添加时间戳破坏缓存，确保加载最新版本
                const urlWithCache = newPreviewUrl.includes('?')
                  ? `${newPreviewUrl}&_t=${Date.now()}`
                  : `${newPreviewUrl}?_t=${Date.now()}`;
                setPreviewUrl(urlWithCache);
                setPreviewKey(k => k + 1); // 刷新预览

                console.log('[ProjectDetail] 微调完成，已更新:', {
                  versionId: latestVersion.id,
                  version: latestVersion.version,
                  previewUrl: newPreviewUrl,
                });

                // 成功提示
                toast.success(`已生成新版本 V${latestVersion.version}`);
              }
            }
          } catch (err) {
            console.error('[ProjectDetail] 刷新版本列表失败:', err);
            toast.error('版本列表刷新失败');
          }
        }
      }
      if (typeof d.fileSize === 'number') setPreviewSize(d.fileSize as number);
    });
    return unsub;
  }, [subscribe, activeSessionId]);

  // 素材实时刷新：AI 生成新资产时推送 asset:ready / audio:ready，bump nonce 触发素材面板重新拉取
  useEffect(() => {
    const bump = () => setMaterialsNonce((n) => n + 1);
    const unsub1 = subscribe('asset:ready' as never, bump);
    const unsub2 = subscribe('audio:ready' as never, bump);
    return () => { unsub1(); unsub2(); };
  }, [subscribe]);

  // 会话标题自动更新：后端生成完成后通过 WebSocket 推送新标题
  useEffect(() => {
    const unsub = subscribe('session:title-updated' as never, (data: unknown) => {
      const d = data as { sessionId: string; title: string };
      if (d.sessionId && d.title) {
        useAgentStore.setState((s) => ({
          sessionList: s.sessionList.map((x) =>
            x.id === d.sessionId ? { ...x, title: d.title } : x
          ),
        }));
      }
    });
    return unsub;
  }, [subscribe]);

  // Batch generation progress (decision C: reuse existing batch:progress/complete WS events)
  useEffect(() => {
    const unsubProgress = subscribe('batch:progress' as never, (data: unknown) => {
      const d = data as { variant?: BatchVariant; progress?: { done: number; total: number } };
      if (!d.variant) return;
      const v = d.variant;
      setBatch((prev) => {
        const total = d.progress?.total ?? prev?.total ?? 0;
        // Pre-fill pending placeholders up to total for a stable grid.
        const list: BatchVariant[] = prev?.variants ? [...prev.variants] : [];
        while (total > 0 && list.length < total) {
          list.push({ index: list.length, name: `变体 ${list.length + 1}`, status: 'pending' });
        }
        const idx = list.findIndex((x) => x.index === v.index);
        if (idx >= 0) list[idx] = { ...list[idx], ...v };
        else list.push({ ...v });
        return { variants: list, done: d.progress?.done ?? list.filter((x) => x.status !== 'pending').length, total, finished: false };
      });
    });
    const unsubComplete = subscribe('batch:complete' as never, (data: unknown) => {
      const d = data as { totalTime?: number; failedCount?: number };
      setBatch((prev) => prev ? { ...prev, finished: true, totalTime: d.totalTime } : prev);
    });
    return () => { unsubProgress(); unsubComplete(); };
  }, [subscribe]);

  // Preview corner drag
  const previewDrag = useRef(false);
  const startPreviewDrag = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    previewDrag.current = true;
    document.body.style.cursor = 'nwse-resize';
    document.body.style.userSelect = 'none';
    const sx = e.clientX; const sy = e.clientY; const sw = previewW;
    const onMove = (ev: MouseEvent) => setPreviewW(Math.min(700, Math.max(MIN_PREVIEW, sw + (ev.clientX - sx))));
    const onUp = () => { previewDrag.current = false; document.body.style.cursor = ''; document.body.style.userSelect = ''; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [previewW]);

  // 点击外部关闭版本下拉菜单
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (versionDropdownRef.current && !versionDropdownRef.current.contains(e.target as Node)) {
        setVersionDropdownOpen(false);
      }
    };
    if (versionDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [versionDropdownOpen]);

  return (
    <div className="relative isolate flex h-dvh flex-col bg-background">
      {/* Top bar */}
      <div className="flex shrink-0 items-center gap-2 border-b border-border/60 px-3 py-1.5">
        <button onClick={() => navigate('/creation')} className="icon-btn rounded-lg p-1.5" title="返回创作中心">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          {editingTitle ? (
            <input autoFocus className="input-neon w-48 px-2 py-0.5 text-xs" value={titleValue}
              onChange={e => setTitleValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { saveTitle(); } if (e.key === 'Escape') setEditingTitle(false); }}
              onBlur={saveTitle} />
          ) : (
            <span className="text-base font-medium text-foreground cursor-default"
              onDoubleClick={() => { setTitleValue(projectName || '项目详情'); setEditingTitle(true); }}
              title="双击编辑名称">
              {projectName || '项目详情'}
            </span>
          )}
        </div>
        {/* 管理所有试玩按钮 */}
        {projectId && (
          <button
            onClick={() => navigate(`/project/${projectId}/playables`)}
            className="flex items-center gap-1.5 rounded-lg border border-neon-cyan/50 bg-neon-cyan/10 px-3 py-1.5 text-xs text-neon-cyan transition-all hover:border-neon-cyan hover:bg-neon-cyan/20"
            title="查看项目所有已发布的试玩">
            <List size={12} />
            已上传OSS试玩
          </button>
        )}
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Far left: Session list (project's sessions) — 背景提一档与页面拉开边界 */}
        <div className="flex shrink-0 flex-col border-r border-border/60 bg-card/40">
          <SessionSidebar activeSessionId={activeSessionId} onSelect={handleSelectSession} onCreate={handleCreateSession} />
        </div>

        {/* Left: 工作流（流水线节点） — 有节点时才显示，首次出现时自动展开 */}
        {(Object.keys(pipelineNodes).length > 0
          || pipelinePauseState?.status === 'running'
          || pipelinePauseState?.status === 'paused') && (
          <div className="relative flex">
            <div className={cn('flex flex-col border-r border-border/60 bg-card/30 transition-all duration-300 ease-in-out overflow-hidden', pipelineCollapsed ? 'w-0 border-r-0' : 'w-[230px]')}>
              <div className="flex items-center border-b border-border/60 p-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-foreground/90 whitespace-nowrap">工作流</span>
              </div>
              <div className="flex-1 overflow-y-auto px-2 py-2">
                {/* 流水线卡死超时提示（5min 无进度变化） */}
                {pipelineTimedOut && (
                  <div className="mb-2 flex flex-col gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-2">
                    <span className="flex items-center gap-1 text-[11px] font-medium text-amber-400"><AlertTriangle size={12} />执行超时</span>
                    <span className="text-[10px] text-muted-foreground">流水线已 5 分钟无进度更新，可刷新页面重试。</span>
                    <button onClick={() => window.location.reload()} className="mt-0.5 flex items-center gap-1 self-start rounded border border-amber-500/40 px-2 py-0.5 text-[10px] text-amber-400 hover:bg-amber-500/15">
                      <RotateCw size={10} />刷新重试
                    </button>
                  </div>
                )}
                {!sessionReady ? (
                  <div className="flex flex-col items-center gap-3 py-12 text-muted-foreground"><Loader2 size={24} className="animate-spin text-neon-cyan" /><span className="text-sm">初始化流程...</span></div>
                ) : (
                  <Pipeline
                    nodes={pipelineNodes}
                    pipeline={pipelinePauseState}
                    sessionId={activeSessionId}
                    onNodeClick={(key) => navigate(`/workflow/${activeSessionId}`, { state: { selectedNode: key } })}
                    onResumed={() => refreshPipeline()}
                  />
                )}
              </div>
            </div>
            {/* Floating collapse toggle */}
            <button
              onClick={togglePipeline}
              className="absolute -right-3 top-1/2 z-10 -translate-y-1/2 rounded-full border border-border/50 bg-background p-0.5 shadow-sm hover:border-primary/50 hover:text-primary"
            >
              <ChevronLeft size={14} className={cn('text-muted-foreground transition-transform duration-300', pipelineCollapsed && 'rotate-180')} />
            </button>
          </div>
        )}

        {/* Center: Preview — hidden on smaller screens */}
        <div className="hidden flex-1 flex-col xl:flex overflow-hidden">
          {!sessionReady ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground p-4">
              {initError ? (
                <>
                  <AlertTriangle size={28} className="text-amber-400" />
                  <span className="text-sm">会话初始化失败，请检查后端服务后重试</span>
                  <button onClick={initProject} className="flex items-center gap-1.5 rounded-lg border border-border/60 px-3 py-1.5 text-sm text-foreground/85 hover:border-primary/50 hover:text-primary">
                    <RotateCw size={13} />重试
                  </button>
                </>
              ) : (
                <>
                  <Loader2 size={28} className="animate-spin text-neon-cyan" />
                  <span className="text-sm">正在初始化项目会话...</span>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Preview Toolbar — 固定顶部 */}
              <div className="flex shrink-0 items-center justify-between border-b border-border/30 px-4 py-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">游戏预览</span>

                  {/* 版本选择器 */}
                  {playableVersions.length > 0 && (
                    <div className="relative" ref={versionDropdownRef}>
                      <button
                        onClick={() => setVersionDropdownOpen(!versionDropdownOpen)}
                        className="flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs text-primary transition-colors hover:border-primary/50 hover:bg-primary/15"
                      >
                        <span className="font-medium">
                          {playableVersions.find(v => v.id === currentVersionId)?.version
                            ? `V${playableVersions.find(v => v.id === currentVersionId)?.version}`
                            : '选择版本'}
                        </span>
                        <ChevronDown size={12} className={cn('transition-transform', versionDropdownOpen && 'rotate-180')} />
                      </button>

                      {/* 版本下拉菜单 */}
                      {versionDropdownOpen && (
                        <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-lg border border-border/50 bg-card shadow-2xl">
                          <div className="max-h-[400px] overflow-y-auto p-1.5">
                            {playableVersions.map((version) => {
                              const isActive = version.id === currentVersionId;
                              const versionNum = version.version;
                              const createdAt = new Date(version.createdAt).toLocaleString('zh-CN', {
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              });

                              return (
                                <div
                                  key={version.id}
                                  className={cn(
                                    'group flex items-center gap-2 rounded-lg px-2.5 py-2 transition-colors cursor-pointer',
                                    isActive ? 'bg-primary/15' : 'hover:bg-muted/60'
                                  )}
                                  onClick={() => {
                                    handleVersionChange(version.id);
                                    setVersionDropdownOpen(false);
                                  }}
                                >
                                  {version.coverUrl ? (
                                    <div className="h-10 w-8 shrink-0 overflow-hidden rounded-md border border-border/50 bg-muted/30">
                                      <img
                                        src={version.coverUrl}
                                        alt={`V${versionNum} 封面`}
                                        loading="lazy"
                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        className="h-full w-full object-cover"
                                      />
                                    </div>
                                  ) : (
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border/50 bg-muted/30">
                                      {isActive ? (
                                        <Check size={14} className="text-primary" />
                                      ) : (
                                        <span className="text-[11px] font-semibold text-foreground/70">V{versionNum}</span>
                                      )}
                                    </div>
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                      <span className={cn('text-sm font-medium', isActive ? 'text-primary' : 'text-foreground')}>
                                        V{versionNum}
                                      </span>
                                      {version.variantLabel && (
                                        <span className="truncate text-xs text-muted-foreground">· {version.variantLabel}</span>
                                      )}
                                    </div>
                                    {/* 对话描述（生成/微调指令） */}
                                    {version.description && (
                                      <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground/90" title={version.description}>
                                        {version.description}
                                      </p>
                                    )}
                                    <span className="text-[10px] text-muted-foreground">{createdAt}</span>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      confirm(
                                        `确定删除版本 V${versionNum}？`,
                                        '删除后无法恢复',
                                        async () => {
                                          try {
                                            const res = await authFetch(`/api/playables/${version.id}`, { method: 'DELETE' });
                                            const data = await res.json();
                                            if (data?.success) {
                                              const newVersions = playableVersions.filter(v => v.id !== version.id);
                                              setPlayableVersions(newVersions);
                                              if (currentVersionId === version.id && newVersions.length > 0) {
                                                const latestVersion = newVersions[0];
                                                setCurrentVersionId(latestVersion.id);
                                                currentVersionIdRef.current = latestVersion.id;
                                                setPreviewUrl(latestVersion.previewUrl || latestVersion.htmlUrl);
                                                setPreviewKey(k => k + 1);
                                              }
                                              toast.success(`版本 V${versionNum} 已删除`);
                                            } else {
                                              toast.error('删除失败：' + (data?.message || '未知错误'));
                                            }
                                          } catch (err) {
                                            console.error('删除版本失败:', err);
                                            toast.error('删除失败，请重试');
                                          }
                                        }
                                      );
                                    }}
                                    className="opacity-0 transition-opacity group-hover:opacity-70 hover:!opacity-100 p-1 rounded hover:bg-red-500/10 hover:text-red-500"
                                    title="删除版本"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                </div>
                <div className="flex items-center gap-1">
                  <Tooltip content={publishing ? '发布中...' : '发布试玩'}>
                    <button onClick={handlePublish} disabled={publishing || !previewUrl} className={cn('icon-btn rounded p-1 disabled:opacity-30', publishing && 'animate-pulse')}><Send size={14} /></button>
                  </Tooltip>
                  <Tooltip content={previewUrl ? '新页面查看' : '生成完成后可查看'}>
                    <button onClick={() => { if (previewUrl) window.open(previewUrl, '_blank'); }} disabled={!previewUrl} className="icon-btn rounded p-1 disabled:opacity-30"><Eye size={14} /></button>
                  </Tooltip>
                  {/* DO-NOT-REVERT: BUG-20260805-014 — Link/QrCode 生成后即可用（disabled={!previewUrl}），禁止改回 !published；工具栏禁止出现 Upload 按钮 */}
                  <Tooltip content={previewUrl ? '复制分享链接' : '生成完成后可 复制链接'}>
                    <button onClick={() => { navigator.clipboard.writeText(shareUrl ?? ''); toast.success('已复制'); }} disabled={!previewUrl} className="icon-btn rounded p-1 disabled:opacity-30"><Link size={14} /></button>
                  </Tooltip>
                  <Tooltip content={previewUrl ? '下载试玩 HTML' : '生成完成后可下载'}>
                    <button onClick={() => setShowDownload(true)} disabled={!previewUrl} className="icon-btn rounded p-1 disabled:opacity-30"><Download size={14} /></button>
                  </Tooltip>
                  <Tooltip content={previewUrl ? '扫描二维码在手机上试玩' : '生成完成后可扫码试玩'}>
                    <button onClick={() => setShowQr(true)} disabled={!previewUrl} className="icon-btn rounded p-1 disabled:opacity-30"><QrCode size={14} /></button>
                  </Tooltip>
                  <Tooltip content={previewUrl && gddConfirmed ? '基于当前方案重新生成' : '生成完成后可重新生成'}>
                    <button onClick={handleRegenerate} disabled={!previewUrl || !gddConfirmed || pipelineTaskRunning || streaming} className="icon-btn rounded p-1 disabled:opacity-30"><Sparkles size={14} /></button>
                  </Tooltip>
                  <Tooltip content={previewUrl ? '刷新预览' : '生成完成后可刷新预览'}>
                    <button onClick={() => setPreviewKey(k => k + 1)} disabled={!previewUrl} className="icon-btn rounded p-1 disabled:opacity-30"><RotateCw size={14} /></button>
                  </Tooltip>
                </div>
              </div>
              {/* 内容区 — 可滚动 */}
              <div className="flex-1 overflow-auto p-4">
                <div className="flex flex-col items-center gap-3" style={{ width: previewW, margin: '0 auto' }}>
                  <PreviewFrame
                    key={previewKey}
                    ref={previewFrameRef}
                    src={previewUrl}
                    loading={(isCurrentSessionLoading || messagesLoading) && !previewUrl}
                    statusText={previewStatusText}
                    showSpinner={previewShowSpinner}
                    onAcceptDraft={handleAcceptDraft}
                    onDiscardDraft={handleDiscardDraft}
                    onInsertPrompt={(text) => setPrefill({ text, nonce: Date.now() })}
                  />

                  {batch && batch.total > 0 && (
                    <div className="w-full">
                      <BatchVariantGrid variants={batch.variants} done={batch.done} total={batch.total}
                        finished={batch.finished} totalTime={batch.totalTime}
                        onOpen={(url) => { setPreviewUrl(url); }} />
                    </div>
                  )}

                  {/* Project materials */}
                  {activeSessionId && (
                    <div className="w-full">
                      <button onClick={() => setShowMaterials(v => !v)}
                        className="mb-3 flex w-full items-center gap-3 px-1 py-1.5 text-sm font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground">
                        <ChevronLeft size={16} className={cn('transition-transform', showMaterials ? '-rotate-90' : 'rotate-180')} />
                        本项目素材
                      </button>
                      {showMaterials && <MaterialPanel sessionId={activeSessionId} refreshKey={materialsNonce} onPreviewUpdated={(url, newVersionId) => {
                                            if (newVersionId && activeSessionId) {
                                              // 替换接口已经返回新版本 ID，立即保存；即使后续列表请求尚未完成就刷新也能恢复。
                                              // 先写竞态屏障，再等待版本列表请求；否则旧微调 effect 可能在
                                              // 请求返回前把预览锚回旧版本。
                                              const supersededRefineId = useAgentStore.getState().refineState.playableId;
                                              if (supersededRefineId) {
                                                sessionStorage.setItem(`manual_replace_refine_${activeSessionId}`, supersededRefineId);
                                              }
                                              sessionStorage.setItem(`manual_replace_${activeSessionId}`, newVersionId);
                                              // 让任何在途的旧版本列表请求失效；否则它返回后可能把 V9 的
                                              // 选择状态覆盖成旧的 V8。
                                              const replaceRequestId = ++versionsRequestRef.current;
                                              skipRefineAnchorRef.current = newVersionId;
                                              // BUG-20260810-060: 替换产生新版本——重载版本列表并切换到新版本，
                                              // 保证每个版本保留独立预览映射（旧版本切回仍看旧产物/旧快照）
                                              (async () => {
                                                try {
                                                  await persistVersionId(activeSessionId, newVersionId);
                                                  const vRes = await authFetch(`/api/agent/session/${activeSessionId}/playables`);
                                                  const vData = await vRes.json();
                                                  if (replaceRequestId !== versionsRequestRef.current
                                                    || useAgentStore.getState().activeSessionId !== activeSessionId) return;
                                                  if (vData?.success && Array.isArray(vData.data)) {
                                                    const validVersions = filterValidVersions(vData.data);
                                                    setPlayableVersions(validVersions);
                                                    const nv = validVersions.find((v: any) => v.id === newVersionId);
                                                    if (nv?.previewUrl) {
                                                      setCurrentVersionId(nv.id);
                                                      currentVersionIdRef.current = nv.id;
                                                      setPreviewUrl(nv.previewUrl);
                                                      setPreviewKey(k => k + 1);
                                                      // BUG-FIX: 防止 refinePlayableId effect 拉回旧版本
                                                      // 手动替换产生新版本后，标记跳过微调锚定，避免被旧的 refinePlayableId 拉回
                                                      return;
                                                    }
                                                  }
                                                } catch { /* 重载失败降级直接刷预览 */ }
                                                setPreviewUrl(url);
                                                setPreviewKey(k => k + 1);
                                              })();
                                            } else {
                                              // BUG-20260812-006: 无新版本（注入未命中/建版本失败）也降级刷预览，
                                              // 避免回调 no-op 导致「替换后无任何反应」
                                              setPreviewUrl(url);
                                              setPreviewKey(k => k + 1);
                                            }
                                          }} versionSnapshot={selectedVersion?.assetManifest} snapshotLabel={selectedVersion ? `V${selectedVersion.version}` : undefined} basePlayableId={selectedVersion?.id} />}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Chat resize — 拖拽调整 AI 对话宽度 */}
        <div onMouseDown={chatResize.onMouseDown} className="group flex w-1.5 shrink-0 cursor-col-resize items-center justify-center border-l border-border/60 bg-border/40 hover:bg-primary/30 active:bg-primary/50">
          <GripVertical size={12} className="text-muted-foreground/30 group-hover:text-primary" />
        </div>

        {/* Right: Chat — 可拖拽，默认 600，最大 700 */}
        <div className="flex shrink-0 flex-col border-l border-border/60 bg-card/40" style={{ width: chatResize.width }}>
          {/* 顶部标题栏 + 流水线状态 */}
          <div className="flex shrink-0 items-center justify-between border-b border-border/60 px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-foreground/90">对话</span>
          </div>

          {/* 对话内容 */}
          <>
            <div className="relative flex-1 overflow-hidden">
              <div ref={chatScrollRef} onScroll={(e) => {
                const el = e.currentTarget;
                setShowScrollToBottom(el.scrollHeight - el.scrollTop - el.clientHeight > 80);
              }} className="h-full overflow-y-auto px-3 py-2">
                {sessionReady ? (
                  messagesLoading ? (
                    <div className="flex flex-col items-center gap-3 py-12 text-muted-foreground">
                      <Loader2 size={24} className="animate-spin text-neon-cyan" />
                      <span className="text-sm">加载历史消息...</span>
                    </div>
                  ) : (
                    <ChatPanel messages={messages} loading={isCurrentSessionLoading} executionState={conversationExecutionState}
                    onConfirmGdd={(msgId) => { setPreviewUrl(undefined); setPreviewSize(undefined); useAgentStore.getState().setGddConfirmed(true); useAgentStore.getState().updateMessageMetadata(msgId, { gddConfirmed: true }); markGddConfirmed(activeSessionId!).catch(() => {}); sendMessage('确认生成', activeSessionId, true, { ...useAgentStore.getState().genConfig, ...(currentVersionId ? { baseVersionId: currentVersionId } : {}) }); }}
                    onChoice={(field, value, msgId) => { useAgentStore.getState().updateMessageMetadata(msgId!, { choiceValue: value }); markChoiceSelected(activeSessionId!, field, value).catch(() => {}); sendMessage(`${value}`, activeSessionId); }}
                    onDuplicateChoice={(choice) => { setPreviewUrl(undefined); setPreviewSize(undefined); sendMessage('确认生成', activeSessionId, true, { ...useAgentStore.getState().genConfig, duplicateChoice: choice, ...(currentVersionId ? { baseVersionId: currentVersionId } : {}) }); }}
                    onVideoConfirm={(messageId) => {
                      useAgentStore.getState().updateMessageMetadata(messageId, { videoConfirmed: true, confirmedAt: new Date().toISOString() });
                      markVideoConfirmed(activeSessionId!).catch(() => {});
                      sendMessage('确认生成', activeSessionId, false);
                    }}
                    onVideoMaterialConfirm={(messageId, videoOrder, imageRoles, videoOrderIndexes) => {
                      // 素材确认：锁定卡片 + 携带调整结果发送确认（后端应用重排/槽位后入队）
                      useAgentStore.getState().updateMessageMetadata(messageId, { materialConfirmed: true, confirmedAt: new Date().toISOString() });
                      // DO-NOT-REVERT: 确认请求不再携带 videoFilePaths——服务端以 gddConfig 落库的
                      // 原始列表 + videoOrderIndexes 重排，避免前端旧顺序覆盖用户拖拽结果
                      const { videoFilePaths: _drop, ...restGenConfig } = useAgentStore.getState().genConfig;
                      sendMessage('确认生成', activeSessionId, false, {
                        ...restGenConfig,
                        videoMaterialAdjustment: { videoOrder, imageRoles, videoOrderIndexes },
                      });
                    }}
                    onVideoPlanConfirm={(messageId) => {
                      // 视频方案确认：锁定卡片（前端内存 + 后端持久化）+ 通知后端进入阶段三（入队生成）
                      useAgentStore.getState().updateMessageMetadata(messageId, { planConfirmed: true, confirmedAt: new Date().toISOString() });
                      // BUG-20260908-PW-001: 必须调后端 mark 接口持久化 planConfirmed——
                      // 否则刷新后卡片回到未确认态、倒计时重启，自动重发「确认生成」
                      markVideoPlanConfirmed(activeSessionId!).catch(() => {});
                      sendMessage('确认生成', activeSessionId, false, { videoPlanConfirm: true });
                    }}
                    onRetry={(messageId) => {
                      const msgs = useAgentStore.getState().messages;
                      const errorIndex = msgs.findIndex(m => m.id === messageId);
                      if (errorIndex === -1) return;
                      // 向前查找最近的用户消息
                      let userContent: string | null = null;
                      for (let i = errorIndex - 1; i >= 0; i--) {
                        if (msgs[i].role === 'user') {
                          userContent = msgs[i].content;
                          break;
                        }
                      }
                      if (!userContent) return;
                      // 先移除错误消息再重新发送（isRetry=true 跳过追加用户消息，避免重复气泡）
                      useAgentStore.getState().setMessages(msgs.filter(m => m.id !== messageId));
                      sendMessage(userContent, activeSessionId, false, undefined, true);
                    }}
                    // BUG-20260908-PW-001: 管线已 running/done 时禁方案卡倒计时自动重发（方案 C 防线）
                    videoPipelineStatus={pipelinePauseState?.status as 'running' | 'done' | 'paused' | null | undefined}
                  />
                  )
                ) : (
                  <EmptyState icon={<Play size={32} />} title="等待会话就绪" description="初始化完成后即可开始对话" />
                )}
              </div>
              {showScrollToBottom && (
                <button
                  type="button"
                  onClick={() => {
                    chatScrollRef.current?.scrollTo({ top: chatScrollRef.current.scrollHeight, behavior: 'smooth' });
                  }}
                  className="absolute bottom-3 left-1/2 z-10 flex h-7 w-7 -translate-x-1/2 items-center justify-center rounded-full border border-border/50 bg-card/90 text-muted-foreground shadow-md backdrop-blur transition-colors hover:border-primary/50 hover:text-foreground"
                  title="回到底部"
                >
                  <ArrowDown size={14} />
                </button>
              )}
            </div>
              <div className="shrink-0 border-t border-border/60 px-3 py-2">
                <div className="mb-1.5 flex flex-wrap items-center gap-3">
                  <span className="text-xs text-muted-foreground">快捷指令</span>
                  <button onClick={() => setShowAnimations(true)} className="flex items-center gap-1.5 rounded text-sm text-foreground/85 hover:text-neon-cyan"><Film size={14} />动画</button>
                  <button onClick={() => setShowParticles(true)} className="flex items-center gap-1.5 rounded text-sm text-foreground/85 hover:text-neon-cyan"><Sparkles size={14} />粒子特效</button>
                  <button onClick={() => setShowTemplates(true)} className="flex items-center gap-1.5 rounded text-sm text-foreground/85 hover:text-neon-cyan"><Gamepad2 size={14} />玩法清单</button>
                  {/* BUG-20260820-WEB-001: 网赚红包全局开关（原玩法清单每卡🧧按钮迁移至此）
                      DO-NOT-REVERT: 禁止改回玩法清单每卡🧧小圆钮方案 */}
                  <Tooltip content={hongbaoOn
                    ? '已开启：玩法清单选择 / 直接发送的描述将叠加网赚红包特效层（进度条 + 红包弹窗 + 金币飞入）'
                    : '开启后，玩法清单选择 / 直接发送的描述将叠加网赚红包特效层（进度条 + 红包弹窗 + 金币飞入）'}>
                    <button
                      onClick={() => {
                        const next = !hongbaoOn;
                        setHongbaoOn(next);
                        // BUG-20260820-WEB-002: 先选快捷指令后开开关 → 末尾追加特效层；关开关 → 移除已追加内容
                        setPrefill(next
                          ? { text: HONGBAO_EFFECT_PROMPT, nonce: Date.now(), mode: 'append-hongbao' }
                          : { text: '', nonce: Date.now(), mode: 'remove-hongbao' });
                      }}
                      className={cn(
                        'flex items-center gap-1.5 rounded text-sm transition-colors',
                        hongbaoOn ? 'text-red-400' : 'text-foreground/85 hover:text-red-400',
                      )}
                    >
                      <span className="text-base leading-none">🧧</span>
                      网赚红包
                      <span className={cn('relative inline-flex h-3.5 w-6 shrink-0 items-center rounded-full transition-colors', hongbaoOn ? 'bg-red-500' : 'bg-muted/60')}>
                        <span className={cn('inline-block h-2.5 w-2.5 transform rounded-full bg-white shadow transition-transform', hongbaoOn ? 'translate-x-3' : 'translate-x-0.5')} />
                      </span>
                    </button>
                  </Tooltip>
                  <button onClick={() => setPrefill({ text: VIDEO_TO_PLAYABLE_PROMPT, nonce: Date.now() })} className="flex items-center gap-1.5 rounded text-sm text-foreground/85 hover:text-neon-cyan"><Video size={14} />视频转试玩</button>
                </div>
                <div className="mb-1.5"><GenerationConfigBar sessionId={activeSessionId} /></div>
                {/* DO-NOT-REVERT(BUG-20260729-002): 会话未就绪时禁用输入区，与 handleSend 守卫双重兜底 */}
                {/* DO-NOT-REVERT(BUG-20260729-003): streaming/loading 必须按会话隔离派生，不得直接用全局值 */}
                <ChatInput
                  onSend={handleSend}
                  onStop={handleStop}
                  queuedItems={activeSessionId ? (pendingSendsBySession[activeSessionId] ?? []) : []}
                  onRemoveQueued={removeQueuedSend}
                  loading={isCurrentSessionLoading}
                  streaming={isCurrentSessionStreaming}
                  processing={pipelineTaskRunning}
                  processingLabel={pipelineProcessingLabel}
                  prefill={prefill}
                  disabled={!sessionReady}
                  sessionId={activeSessionId}
                />
              </div>
            </>
        </div>
      </div>
      <AnimationPicker open={showAnimations} onClose={() => setShowAnimations(false)}
        onSelect={(name) => {
          setPrefill({ text: `帮我增加${name}动画`, nonce: Date.now() });
        }} />
      <ParticleCatalog open={showParticles} onClose={() => setShowParticles(false)}
        onSelect={(name) => {
          setPrefill({ text: `使用粒子特效: ${name}`, nonce: Date.now() });
        }} />
      <PublishDialog
        open={publishDialogOpen}
        defaultName={generateDefaultPublishName()}
        onConfirm={handlePublishConfirm}
        onCancel={() => setPublishDialogOpen(false)}
        loading={publishing}
      />
      {confirmDialog}
      <DownloadDialog open={showDownload} onOpenChange={setShowDownload} sessionId={activeSessionId} fileSize={previewSize} />
      <GameplaySelector open={showTemplates} onClose={() => setShowTemplates(false)}
        onSelect={(key, label, variant) => {
          // key 为玩法英文 key（如 match3、runner），用于查询 GAMEPLAY_PROMPTS
          const basePrompt = getGameplayPrompt(key) ?? `做一个${label}类型的游戏`;
          // BUG-20260820-WEB-002: 开关 ON 且非内置红包变体 → 预填含特效层；
          // OFF 或内置红包变体 → 纯 basePrompt（输入框不出现任何红包内容/标记，
          // 内置变体抑制改由 handleSend 发送期隐式注入 [NO_EARN_FX]）
          const isBuiltinHongbao = !!variant && variant.id.startsWith('hongbao');
          lastVariantRef.current = variant ?? null;
          const prompt = (hongbaoOn && !isBuiltinHongbao)
            ? basePrompt + HONGBAO_EFFECT_PROMPT
            : basePrompt;
          // Cocos 角标变体 → 显式切换到 cocos-generation 模式（独立于模板/蓝图入口）；
          // 非 Cocos 变体 → 仅当当前处于 cocos-generation 时回退到 auto，避免覆盖用户其他显式选择
          const currentMode = useAgentStore.getState().genConfig.generationMode ?? 'auto';
          if (variant?.engineBadge === 'cocos') {
            if (currentMode !== 'cocos-generation') {
              useAgentStore.getState().setGenConfig({ generationMode: 'cocos-generation' });
            }
          } else if (currentMode === 'cocos-generation') {
            useAgentStore.getState().setGenConfig({ generationMode: 'auto' });
          }
          setPrefill({ text: prompt, nonce: Date.now() });
        }} />

      {showQr && shareUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowQr(false)}>
          <div className="glass-card flex flex-col items-center gap-4 p-8" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-foreground">扫码试玩</h3>
            <div className="rounded-xl bg-white p-4">
              <QRCodeSVG value={shareUrl} size={200} />
            </div>
            <p className="text-xs text-muted-foreground">用手机扫描二维码即可试玩</p>
          </div>
        </div>
      )}
    </div>
  );
}
